// Device labels carry a bare 15-digit IMEI (Luhn check digit). Mirrors backend src/utils/imei.js parseScan.
export const IMEI_LENGTH = 15;
const MAX_SCAN_LENGTH = 200;

// Trims surrounding whitespace, the scanner's trailing CR/LF/Tab and other leading/trailing control characters.
export const normalizeScan = (raw) =>
    String(raw == null ? '' : raw)
        .slice(0, MAX_SCAN_LENGTH)
        .replace(/^[\s\u0000-\u001f\u007f]+|[\s\u0000-\u001f\u007f]+$/g, ''); // eslint-disable-line no-control-regex

export const isLuhnValid = (digits) => {
    const s = String(digits);
    if (!/^\d+$/.test(s)) return false;
    let sum = 0;
    for (let i = 0; i < s.length; i += 1) {
        let d = Number(s[s.length - 1 - i]);
        if (i % 2 === 1) {
            d *= 2;
            if (d > 9) d -= 9;
        }
        sum += d;
    }
    return sum % 10 === 0;
};

export const isValidImei = (value) => /^\d{15}$/.test(String(value)) && isLuhnValid(value);

const gs1Values = (code) => {
    const values = [];
    const bracketed = /\((01|21)\)([^(\u001d]*)/g; // eslint-disable-line no-control-regex
    let m = bracketed.exec(code);
    while (m) {
        values.push(m[2].trim());
        m = bracketed.exec(code);
    }
    if (!values.length) {
        const r = code.replace(/^\][A-Za-z]\d/, '').match(/^01(\d{14})21([^\u001d]{1,20})/); // eslint-disable-line no-control-regex
        if (r) values.push(r[1], r[2]);
    }
    return values;
};

const looksLikeGs1 = (code) => /\((01|21)\)/.test(code) || /^(\][A-Za-z]\d)?01\d{14}21/.test(code);

// Returns { imei } or { error }. GS1 (21)/(01) is a fallback only and must still yield one valid IMEI.
export const parseScan = (raw) => {
    const code = normalizeScan(raw);
    if (!code) return { error: 'Scan or type a device IMEI' };
    if (/^\d+$/.test(code)) {
        if (code.length !== IMEI_LENGTH) return { error: `IMEI must be exactly ${IMEI_LENGTH} digits (got ${code.length})` };
        if (!isLuhnValid(code)) return { error: 'Invalid IMEI: check digit does not match (rescan or retype)' };
        return { imei: code };
    }
    if (looksLikeGs1(code)) {
        const found = [...new Set(gs1Values(code).filter(isValidImei))];
        if (found.length === 1) return { imei: found[0] };
        if (found.length > 1) return { error: 'Barcode contains more than one IMEI; type the 15-digit IMEI instead' };
        return { error: 'Barcode does not contain a valid 15-digit IMEI' };
    }
    return { error: `Not an IMEI: scan the device IMEI barcode (${IMEI_LENGTH} digits)` };
};
